import axios from "axios";
import store from "../store";
import { setNotificationAction, setToastAction } from "../reducers/mmymSlice";
import { setUserAction, setUserProfile } from "../reducers/userSlice";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;
axios.defaults.baseURL = API_BASE_URL;

/* =======================
   PUBLIC API ALLOW LIST
======================= */
const PUBLIC_API_ROUTES = [
  "public/",
  "auth/client-signin",
  "auth/forgot-password",
  "auth/reset-password",
  "auth/signup",
  "auth/verify-otp",
  "auth/resend-otp",
  "public/customer",
  "public/customer/",
  "public/customer/verify-token",
];

const isPublicAPI = (url: string) =>
  PUBLIC_API_ROUTES.some((route) => url.startsWith(route));

/* =======================
   MAIN API CALL
======================= */
export function callAPI(
  payload: any,
  method: any,
  url: any,
  contentType: any = "",
  requestConfig: any = {}
) {
  const userDetail = localStorage.getItem("user");

  /* 🚫 BLOCK ONLY IF API IS NOT PUBLIC */
  if (!userDetail && !isPublicAPI(url)) {
    store.dispatch(setUserAction([]));
    store.dispatch(setUserProfile([]));
    window.location.href = "/";
    return false;
  }

  /* 🚫 BLOCK IF AUTH HEADER MISSING (PRIVATE APIs ONLY) */
  if (!isPublicAPI(url) && !axios.defaults.headers.common["Authorization"]) {
    localStorage.removeItem("user");
    store.dispatch(setUserAction([]));
    store.dispatch(setUserProfile([]));
    window.location.href = "/";
    return false;
  }

  const authHeader = axios.defaults.headers.common["Authorization"];

  /* ✅ Dynamic token support for public APIs */
  const dynamicHeaders: any = {};
  if (payload?.token) {
    dynamicHeaders["Authorization"] = `Bearer ${payload.token}`;
  }

  /* ✅ Handle content-type override */
  if (contentType) {
    delete axios.defaults.headers.common["Authorization"];
    axios.defaults.headers.common["Content-Type"] =
      contentType || "application/json";
  }

  /* ✅ Explicit baseURL resolution
     - if the caller passed requestConfig.baseURL (e.g. v2 endpoint), use that
     - otherwise fall back to the global default (v1)
     This guarantees a per-call override actually takes effect, instead of
     silently depending on axios's internal config merge order. */
  const resolvedBaseURL = requestConfig?.baseURL || axios.defaults.baseURL;
 
  if (process.env.NODE_ENV !== "production") {
    // Debug helper — remove once you've confirmed v1/v2 routing works as expected
    console.log(
      `[callAPI] ${String(method).toUpperCase()} ${url} -> baseURL: ${resolvedBaseURL}`
    );
  }


  return axios({
    method,
    url,
    data: payload?.payload,
    ...requestConfig,
     baseURL: resolvedBaseURL,
    headers: {
      ...dynamicHeaders,
    },
  })
    .then((res) => {
      if (contentType) {
        axios.defaults.headers.common["Authorization"] = authHeader;
        axios.defaults.headers.common["Content-Type"] = "application/json";
      }
      return res.data;
    })
    .catch((err) => {
      if (contentType) {
        axios.defaults.headers.common["Authorization"] = authHeader;
        axios.defaults.headers.common["Content-Type"] = "application/json";
      }
      throw err;
    });
}

/* =======================
   ERROR HANDLER
======================= */
export const handleError = (error: any, statusCodes?: any) => {
  switch (error?.status) {
    case 400:
    case 403:
    case 404:
    case 500:
    case 409:
      if (!statusCodes || statusCodes.includes(error.status)) {
        store.dispatch(
          setNotificationAction({
            message:
              typeof error?.data?.message === "string"
                ? [error.data.message]
                : error?.data?.message,
            severity: "error",
          })
        );
      }
      return;

    case 401:
      store.dispatch(
        setToastAction({
          message: "Logging out due to unauthorized access.",
          appearance: "error",
        })
      );
      setTimeout(() => {
        localStorage.removeItem("user");
        localStorage.removeItem("persist:root");
        store.dispatch(setUserAction([]));
        store.dispatch(setUserProfile([]));
        window.location.href = "/";
      }, 1000);
      return;

    default:
      if (error?.code === "ERR_NETWORK") {
        window.alert("502 - " + error?.config?.url);
      }
      return store.dispatch(
        setNotificationAction({ message: [], severity: "error" })
      );
  }
};

/* =======================
   SUCCESS HANDLER
======================= */
export const handleSuccess = (response: any) => {
  const message = response?.message || "Operation successful";

  switch (response?.statusCode) {
    case 200:
    case 201:
      store.dispatch(
        setToastAction({ message, appearance: "success" })
      );
      return;

    case 204:
      store.dispatch(
        setToastAction({ message: "No Content", appearance: "info" })
      );
      return;

    default:
      store.dispatch(
        setToastAction({ message, appearance: "success" })
      );
      return;
  }
};
